import { computed, ComputedRef, Ref, ref } from 'vue'
import { Remark } from '@app/application/communication/types'
import { partition } from 'lodash'
import api from '@app/application/client_meetings/screens/preparation/api'
import { SfLog } from '@app/adapters/logger'

export default function useOrderedRemarks(
  remarks: ComputedRef<Remark[]>,
  initialOrder: number[]
): {
  orderedRemarks: ComputedRef<Remark[]>
  unorderedRemarks: ComputedRef<Remark[]>
  allRemarks: ComputedRef<Remark[]>
  settingOrder: Ref<boolean>
  setOrder: (newOrder: number[]) => Promise<void>
} {
  const order = ref<number[]>(initialOrder)
  const settingOrder = ref<boolean>(false)

  async function setOrder(newOrder: number[]): Promise<void> {
    order.value = newOrder
    settingOrder.value = true
    try {
      await api.setOrder(newOrder)
    } catch (e) {
      SfLog.warn('Failed to set order of client meeting', {
        extra: {
          error: e,
          order,
        },
      })
    } finally {
      settingOrder.value = false
    }
  }

  const splitRemarks = computed(() => {
    const [inOrder, outOrder] = partition(remarks.value, (r) =>
      order.value.includes(r.id)
    )
    inOrder.sort(
      (r1, r2) => order.value.indexOf(r1.id) - order.value.indexOf(r2.id)
    )

    // If there are no remarks in the order, we want to consider them
    // all as ordered and don't trigger any special behaviour
    if (inOrder.length > 0) {
      return [inOrder, outOrder]
    } else {
      return [outOrder, inOrder]
    }
  })

  const orderedRemarks = computed({
    get: () => splitRemarks.value[0],
    set: (newOrderedRemarks) => {
      if (settingOrder.value) {
        return
      }
      const ids = newOrderedRemarks.map((r) => r.id)
      const newOrder = []
      for (const remark of unorderedRemarks.value) {
        if (!ids.includes(remark.id)) {
          newOrder.push(remark.id)
        }
      }
      newOrder.push(...ids)
      void setOrder(newOrder)
    },
  })
  const unorderedRemarks = computed({
    get: () => splitRemarks.value[1],
    set: (newUnorderedRemarks) => {
      if (settingOrder.value) {
        return
      }
      const ids = newUnorderedRemarks.map((r) => r.id)
      const newOrder = []
      newOrder.push(...ids)
      for (const remark of orderedRemarks.value) {
        if (!ids.includes(remark.id)) {
          newOrder.push(remark.id)
        }
      }
      void setOrder(newOrder)
    },
  })
  const allRemarks = computed(() =>
    ([] as Remark[]).concat(unorderedRemarks.value, orderedRemarks.value)
  )

  return {
    orderedRemarks,
    unorderedRemarks,
    allRemarks,
    setOrder,
    settingOrder,
  }
}
